import React, { useState } from 'react';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import TextField from '@material-ui/core/TextField';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { FormattedMessage } from 'react-intl';
import useIntl from '@afosto/utils-react/intl/useIntl';
import useDataSourceContext from '../DataSource/useDataSourceContext';
import Icon from '../Icon';
import translations from './translations';
import styled from 'styled-components';
import ContentDrawer from '../ContentDrawer';
import reorderArray from '@afosto/utils/array/reorderArray';

const DragDropGrid = styled.div`
  display: grid;
  flex: 1 1 100%;
  grid-template-columns: repeat(2, 1fr);
  grid-column-gap: ${props => props.theme.spacing()}px;
`;

const DroppableContainer = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid #ccc;
  padding: ${props => props.theme.spacing()}px;
`;

const DroppableTitle = styled.div`
  color: #999;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${props => props.theme.spacing()}px;
  min-height: 30px;
`;

const DroppableZone = styled.div`
  flex: 1;
`;

const SearchField = styled(TextField)`
  && {
    margin-bottom: 6px;
    margin-top: 0;
  }
`;

const FixedColumn = styled.div`
  background: #fff;
  border: 1px solid #e0e0e0;
  margin-bottom: ${props => props.theme.spacing()}px;
  padding: ${props => props.theme.spacing()}px;
`;

const ActionContainer = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: ${props => props.theme.spacing(2)}px;
`;

const DraggableContainer = styled.div`
  && {
    background: ${props => (props.isDragging ? '#999' : '#e0e0e0')};
    border: 1px solid #ccc;
    align-items: center;
    margin-bottom: ${props => props.theme.spacing()}px;
    padding: ${props => props.theme.spacing()}px;
    height: 42px;
    user-select: none;
    display: flex;
  }
`;

const DraggableHeading = styled.div`
  flex: 1;
`;

const DataGridColumnSelection = () => {
  const intl = useIntl();
  const {
    showColumnsSelection,
    setShowColumnsSelection,
    setShowAddColumn,
    dataProperties = {},
    views = {},
  } = useDataSourceContext();
  const {
    selectedProperties,
    setSelectedProperties,
    addSelectedProperty,
    removeSelectedProperty,
    getAvailableProperties: getAvailableColumns,
    fixedProperties,
    setSearchValue,
    sortDirection,
    setSortDirection,
    searchValue,
  } = dataProperties;
  const { defaultView } = views;
  const [showAddColumn] = useState(false);

  const getAvailableProperties = () => {
    const properties = getAvailableColumns();
    const fixedKeys = fixedProperties.map(fixedProperty => fixedProperty.key);

    return properties.filter(property => !fixedKeys.includes(property.key));
  };

  /**
   * Handle the drag event on the column draggable.
   * Repositions a column between lists or in the same list.
   * @param result
   */
  const handleDragEnd = result => {
    const { source, destination } = result;
    const isAvailable = source.droppableId === 'available';

    if (!destination) {
      return;
    }

    if (source.droppableId === destination.droppableId) {
      const start = source.index;
      const end = destination.index;

      if (!isAvailable) {
        const reorderedProperties = reorderArray(selectedProperties, { start, end });
        setSelectedProperties(reorderedProperties);
      }
    } else if (isAvailable) {
      const availableProperties = getAvailableProperties();
      const availableProperty = availableProperties[source.index];

      if (availableProperty) {
        addSelectedProperty(availableProperty.key, destination.index);
      }
    } else {
      const selectedProperty = selectedProperties[source.index];

      if (selectedProperty) {
        removeSelectedProperty(selectedProperty.key);
      }
    }
  };

  /**
   * Move a column from the selected columns to the available columns.
   * @param {string} key
   */
  const handleRemoveSelectedColumn = key => () => {
    removeSelectedProperty(key);
  };

  /**
   * Handle the double click on the column elements.
   * Move the clicked column to the other list.
   * @param {string} key
   * @param {string} droppableId
   */
  const handleDoubleClickColumn = (key, droppableId) => () => {
    const isAvailableList = droppableId === 'available';

    if (isAvailableList) {
      addSelectedProperty(key);
    } else {
      removeSelectedProperty(key);
    }
  };

  /**
   * Search for a column in the available columns.
   * @param {Event} event
   */
  const handleSearchAvailableColumns = event => {
    setSearchValue(event.target.value);
  };

  /**
   * Set the sort direction of the available columns.
   */
  const handleSortAvailableColumns = () => {
    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
  };

  /**
   * Reset the selected columns to the selected columns of the default view.
   */
  const handleClearColumns = () => {
    setSelectedProperties(defaultView.properties || []);
  };

  /**
   * Open the add new column drawer.
   */
  const handleAddColumn = () => {
    setShowColumnsSelection(false);
    setShowAddColumn(true);
  };

  /**
   * Return the columns as draggable elements.
   * @param {array} columnList
   * @param {string} droppableId
   * @returns {array}
   */
  const getDraggables = (columnList, droppableId) =>
    columnList.map((column, idx) => (
      <Draggable
        key={column.key}
        draggableId={column.key}
        index={idx}
        isDragDisabled={column.fixed}
      >
        {(provided, snapshot) => (
          <DraggableContainer
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            isDragging={snapshot.isDragging}
            style={provided.draggableProps.style}
            onDoubleClick={handleDoubleClickColumn(column.key, droppableId)}
          >
            <DraggableHeading>{column.name}</DraggableHeading>
            {droppableId === 'selected' && (
              <div>
                <IconButton size="small" onClick={handleRemoveSelectedColumn(column.key)}>
                  <Icon icon={['fal', 'trash-alt']} size="small" />
                </IconButton>
              </div>
            )}
          </DraggableContainer>
        )}
      </Draggable>
    ));

  return (
    <ContentDrawer
      isOpen={showColumnsSelection}
      handleClose={() => setShowColumnsSelection(false)}
      title={intl.formatMessage(translations.columnSelection.title)}
    >
      <ActionContainer>
        <Button variant="outlined" onClick={handleClearColumns}>
          <FormattedMessage {...translations.columnSelection.resetButton} />
        </Button>
      </ActionContainer>
      <DragDropContext onDragEnd={handleDragEnd}>
        <DragDropGrid>
          <DroppableContainer>
            <DroppableTitle>
              <FormattedMessage {...translations.columnSelection.selectedColumns.label} />
            </DroppableTitle>
            {fixedProperties.map(fixedProperty => (
              <FixedColumn key={fixedProperty.key}>{fixedProperty.name}</FixedColumn>
            ))}
            <Droppable droppableId="selected">
              {provided => (
                <DroppableZone ref={provided.innerRef}>
                  {getDraggables(selectedProperties, 'selected')}
                  {provided.placeholder}
                </DroppableZone>
              )}
            </Droppable>
          </DroppableContainer>
          <DroppableContainer>
            <DroppableTitle>
              <span>
                <FormattedMessage {...translations.columnSelection.availableColumns.label} />
              </span>
              <IconButton size="small" onClick={handleSortAvailableColumns}>
                <Icon icon={['fal', 'sort']} size="small" />
              </IconButton>
            </DroppableTitle>
            <SearchField
              type="text"
              variant="outlined"
              size="small"
              value={searchValue}
              margin="dense"
              placeholder={intl.formatMessage(translations.columnSelection.searchField.placeholder)}
              onChange={handleSearchAvailableColumns}
            />
            <Droppable droppableId="available">
              {provided => (
                <DroppableZone ref={provided.innerRef}>
                  {getDraggables(getAvailableProperties(), 'available')}
                  {provided.placeholder}
                  {showAddColumn && (
                    <Button variant="outlined" fullWidth onClick={handleAddColumn}>
                      <FormattedMessage {...translations.columnSelection.addColumn.label} />
                    </Button>
                  )}
                </DroppableZone>
              )}
            </Droppable>
          </DroppableContainer>
        </DragDropGrid>
      </DragDropContext>
    </ContentDrawer>
  );
};

export default DataGridColumnSelection;
